/**
 * 設定 → 納品件数の予定(2026-09-24 発注者指示「設定画面等で一気に登録できるように」。D-145)。
 * 適用開始日を決めて、**全社の 月〜日 の件数をまとめて登録**する。
 * その日に効いている予定を初期値に出し、変えた会社だけ保存する(会社マスタの `delivery_plan` に足す)。
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@core/auth/AuthProvider';
import NumInput from '@core/components/NumInput';
import { useToast } from '@core/components/Toast';
import { useUnsavedGuard } from '@core/components/UnsavedGuard';
import FilterBar from '@core/components/ui/FilterBar';
import PageHeader from '@core/components/ui/PageHeader';
import Panel from '@core/components/ui/Panel';
import { todayStr } from '@core/lib/date';
import { WEEKDAY_LABELS, deliveryPlanOn, type Company, type DateStr } from '@core/lib/types';
import { formatCount } from '@core/utils/formatters';
import { fetchCompanies, saveDeliveryPlans } from './companyRepo';

const ZERO = [0, 0, 0, 0, 0, 0, 0];

export default function SettingsDeliveryPlanPage() {
  const toast = useToast();
  const { user } = useAuth();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [from, setFrom] = useState<DateStr>(todayStr());
  const [counts, setCounts] = useState<Record<string, number[]>>({});
  const [base, setBase] = useState<Record<string, number[]>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setCompanies(await fetchCompanies());
    } catch (e) {
      toast(e instanceof Error ? e.message : '読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const active = useMemo(() => companies.filter((c) => c.status === 'active'), [companies]);

  // 適用開始日を変えたら、その日に効いている予定を初期値に入れ直す
  useEffect(() => {
    const init: Record<string, number[]> = {};
    for (const c of active) {
      const plan = deliveryPlanOn(c, from);
      init[c.id] = plan ? [...plan.counts] : [...ZERO];
    }
    setCounts(init);
    setBase(JSON.parse(JSON.stringify(init)) as Record<string, number[]>);
  }, [active, from]);

  const changed = useMemo(() => active.filter((c) => JSON.stringify(counts[c.id] ?? ZERO) !== JSON.stringify(base[c.id] ?? ZERO)), [active, counts, base]);
  const guard = useUnsavedGuard(changed.length > 0);

  const setCount = (id: string, day: number, v: string) =>
    setCounts((prev) => ({ ...prev, [id]: (prev[id] ?? [...ZERO]).map((x, i) => (i === day ? Math.max(0, Number(v || 0)) : x)) }));
  const fillWeekdays = (id: string, v: string) => setCounts((prev) => ({ ...prev, [id]: [0, 1, 2, 3, 4].map(() => Math.max(0, Number(v || 0))).concat([prev[id]?.[5] ?? 0, prev[id]?.[6] ?? 0]) }));

  async function save() {
    if (!user) return;
    if (changed.length === 0) return;
    setBusy(true);
    try {
      await saveDeliveryPlans(
        changed.map((c) => ({ id: c.id, plan: { from, counts: counts[c.id] ?? [...ZERO] } })),
        user.id,
      );
      toast(`${changed.length}社の予定を保存しました(${from} から)`);
      await load();
    } catch (e) {
      toast(e instanceof Error ? e.message : '保存に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="screen full">
      {guard.modal}
      <PageHeader title="納品件数の予定" description="会社ごとの曜日別の納品件数を、適用開始日を決めてまとめて登録します(締め作業①の初期値になります)" />
      <FilterBar label="適用開始日">
        <input type="date" className="ui-input" aria-label="適用開始日" value={from} onChange={(e) => e.target.value && setFrom(e.target.value as DateStr)} />
        <span className="ui-hint">この日から次の予定まで、この件数が初期値になります</span>
      </FilterBar>

      <Panel
        title={`曜日別の納品件数(${from} から)`}
        meta={changed.length > 0 ? `未保存 ${formatCount(changed.length, { unit: '社' })}` : `${formatCount(active.length, { unit: '社' })}`}
        note="いまの表には**その日に効いている予定**が入っています。変えた会社だけ保存します。祝日は考えません(必要な日は締め作業で直してください)"
      >
        {loading ? (
          <div className="ui-empty">読み込み中…</div>
        ) : (
          <>
            <div className="table-scroll">
              <table className="fit-table no-stack">
                <thead>
                  <tr>
                    <th>会社</th>
                    {WEEKDAY_LABELS.map((w) => (
                      <th key={w} className="num">
                        {w}
                      </th>
                    ))}
                    <th className="num">週計</th>
                    <th>平日を一括</th>
                  </tr>
                </thead>
                <tbody>
                  {active.map((c) => {
                    const row = counts[c.id] ?? ZERO;
                    const dirty = JSON.stringify(row) !== JSON.stringify(base[c.id] ?? ZERO);
                    return (
                      <tr key={c.id} className={dirty ? 'is-dirty' : undefined}>
                        <td>
                          {c.name}
                          {dirty && <span className="mark-edited">変</span>}
                        </td>
                        {WEEKDAY_LABELS.map((w, d) => (
                          <td key={w} className="num">
                            <NumInput value={String(row[d] ?? 0)} onChange={(v) => setCount(c.id, d, v)} />
                          </td>
                        ))}
                        <td className="num">
                          <b>{formatCount(row.reduce((a, b) => a + b, 0))}</b>
                        </td>
                        <td>
                          <NumInput value="" placeholder="月〜金に一括" onChange={(v) => v !== '' && fillWeekdays(c.id, v)} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="modal-actions" style={{ justifyContent: 'flex-start' }}>
              <button type="button" className="ui-btn ui-btn-primary" disabled={busy || changed.length === 0} onClick={() => void save()}>
                {changed.length > 0 ? `${changed.length}社を保存する` : '保存する'}
              </button>
              <button type="button" className="ui-btn ui-btn-secondary" disabled={busy || changed.length === 0} onClick={() => setCounts(JSON.parse(JSON.stringify(base)) as Record<string, number[]>)}>
                元に戻す
              </button>
            </div>
          </>
        )}
      </Panel>
    </div>
  );
}
